import { Storage } from 'aws-amplify';
import {
  map,
  isNil,
  isEmpty,
  all,
  values,
  compose,
  merge,
  splitEvery,
  mapObjIndexed,
  reject,
  flatten,
  propOr,
  uniq,
} from 'ramda';

import { format } from 'date-fns';
import { ISO_Format_Date2, parseDate } from 'helpers/date';
import { ReportParams } from 'scenes/Report/components/ReportBase/components/ParamsHeader/ParamsHeader';
import { BatchData } from 'components/DownloadWidget/DownloadWidget';

import { textShapeRequestsConfig, BatchConfigData } from './BatchReport.config';
import { ReportProps } from './scenes/GenerationResults/GenerationResults';
import { ValidReports } from './BatchReport';

import performanceOverviewBatchConfig from '../ReportDetail/scenes/PerformanceReports/PerformanceOverview/PerformanceOverview.batch.config';

const MAX_UPLOAD_PER_TIME = 10;
const splitEveryMaxUpload = splitEvery(MAX_UPLOAD_PER_TIME);

const layerBaseConfig = {
  size: {
    width: {
      magnitude: 457.92,
      unit: 'PT',
    },
    height: {
      magnitude: 358.56,
      unit: 'PT',
    },
  },
  transform: {
    scaleX: 1,
    scaleY: 1,
    translateX: 241.92,
    translateY: 23.04,
    unit: 'PT',
  },
};

const imageLayerMapping = ({
  pageId,
  publicImageURL,
  data,
  additionalOptions = layerBaseConfig,
}: ReportProps) => {
  const { size, transform } = layerBaseConfig;
  let elementId = `Image_${pageId}`;

  if (!data) {
    return getCreateImageRequest(
      pageId,
      elementId,
      publicImageURL,
      transform,
      size,
      additionalOptions,
    );
  }

  if ('idx' in data && 'isSegmented' in data && 'replaceTextConfig' in data) {
    const { idx, isSegmented, replaceTextConfig } = data;
    if (isSegmented) {
      const newPageId = `${pageId}_${idx}_copy`;
      elementId = `Image_${newPageId}`;
      return flatten([
        getDuplicateObjectRequest(pageId, newPageId),
        getCreateImageRequest(
          newPageId,
          elementId,
          publicImageURL,
          transform,
          size,
          additionalOptions,
        ),
        updateTextMapping(replaceTextConfig, newPageId, data),
      ]);
    }
  }

  return null;
};

const slideGenerateAndReplaceMapping = (reportProps: ReportProps) => {
  const { data, pageId } = reportProps;
  if (data && propOr(false, 'isSegmented', data)) return null;
  const newPages: Record<string, any>[] = [];
  const response = values(
    mapObjIndexed((datum: BatchData, idx: string) => {
      const newPageId = `${pageId}_${idx}_copy`;
      newPages.push(getDuplicateObjectRequest(pageId, newPageId));
      return values(
        map(
          ({ path, containsText, formatter, defaultValue }: BatchConfigData) =>
            getReplaceTextRequest(
              newPageId,
              formatter(propOr(defaultValue, path, datum)),
              containsText,
            ),
          performanceOverviewBatchConfig,
        ),
      );
    }, data),
  );
  const batchUpdate = newPages.concat(response, [
    getDeleteObjectRequest(pageId),
  ]);
  return flatten(batchUpdate);
};

const getDeleteTextRequest = (objectId: string) => ({
  deleteText: {
    objectId,
    textRange: {
      type: 'ALL',
    },
  },
});

const getReplaceTextRequest = (
  objectId: string,
  replacementText: string,
  containsText?: string,
) => ({
  replaceAllText: {
    containsText: {
      text: containsText,
      matchCase: true,
    },
    replaceText: replacementText,
    pageObjectIds: [objectId],
  },
});

const getDuplicateObjectRequest = (objectId: string, newObjectId: string) => ({
  duplicateObject: {
    objectId,
    objectIds: {
      [objectId]: newObjectId,
    },
  },
});

const getDeleteObjectRequest = (objectId: string) => ({
  deleteObject: {
    objectId,
  },
});

const getInsertTextRequest = (
  objectId: string,
  text: string,
): {
  insertText: { text: string; objectId: string; insertionIndex: number };
} => ({
  insertText: {
    text,
    objectId,
    insertionIndex: 0,
  },
});

const getCreateImageRequest = (
  pageId: string,
  elementId: string,
  publicImageURL?: string,
  transform?: Record<string, unknown>,
  size?: Record<string, unknown>,
  additionalOptions?: {
    size: Record<string, unknown>;
    transform: Record<string, unknown>;
  },
) => ({
  createImage: {
    objectId: elementId,
    url: publicImageURL,
    elementProperties: {
      transform: merge(transform, additionalOptions?.transform),
      size: merge(size, additionalOptions?.size),
      pageObjectId: pageId,
    },
  },
});

const getFormattedDateRange = ({ startDate, endDate }: ReportParams) => {
  if (startDate && endDate) {
    const parsedStartDate = parseDate(startDate, ISO_Format_Date2);
    const parsedEndDate = parseDate(endDate, ISO_Format_Date2);
    if (parsedStartDate && parsedEndDate) {
      return `${format(parsedStartDate, 'MMM dd, yyyy')} to ${format(
        parsedEndDate,
        'MMM dd, yyyy',
      )}`;
    }
  }

  throw new Error('Invalid Date format for start and end dates');
};

const getTemplateURL = (templateId: string): string =>
  `https://docs.google.com/presentation/d/${templateId}/edit`;

const generateImagePath = (imageName: string) =>
  `merchant-portal/report-images/${imageName}-${Date.now()}.png`;

const reportHasImage = ({ imageURL, error }: ReportProps) =>
  !isNil(error) || (!isEmpty(imageURL) && !isNil(imageURL));

const allReportsHaveImages = (reports: ValidReports): boolean =>
  all(reportHasImage, flatten(values(reports) as any));

const uploadImagesMapping = compose(
  values,
  map(({ imageBlob, imageName }: ReportProps) => {
    const imgPath = generateImagePath(imageName);
    return Storage.put(imgPath, imageBlob, {
      contentType: 'image/png',
      bucket: process.env.REPORTS_PUBLIC_BUCKET,
      region: process.env.AWS_REGION,
    }).then(() => ({
      publicImageURL: `${process.env.PUBLIC_ASSETS_URL}${imgPath}`,
    }));
  }),
);

const updateTemplateMapping = compose(
  reject(isNil),
  flatten as any,
  values,
  map((report: ReportProps) => imageLayerMapping(report)),
);

const updateTextMapping = (
  config: BatchConfigData[],
  pageId: string,
  data: Record<string, unknown>,
) =>
  map(
    ({ path, containsText, formatter, defaultValue }: BatchConfigData) =>
      getReplaceTextRequest(
        pageId,
        formatter(propOr(defaultValue, path, data)),
        containsText,
      ),
    config,
  );

const generateSlides = compose(
  reject(isEmpty),
  flatten as any,
  values,
  map((reportProps: ReportProps) =>
    reportProps.data ? slideGenerateAndReplaceMapping(reportProps) : [],
  ),
);

const deletePageMapping = map(({ pageId }: ReportProps) => ({
  deleteObject: {
    objectId: pageId,
  },
}));

const uploadImages = async (
  validReports: Record<string, any>,
): Promise<any[]> => {
  const reports = flatten(values(validReports));
  const uploadImagesToPromise = uploadImagesMapping(reports);
  const imagesId = await Promise.all(uploadImagesToPromise);

  return imagesId;
};

const slideBatchUpdate = async (
  validReports: Record<string, any>,
  invalidReports: ReportProps[],
  templateId: string,
  params: ReportParams,
  googleSlideBatchUpdate: (
    presentationId: string,
    requests: any,
  ) => Promise<any>,
  removeSegmentedBase: Record<string, string>[],
): Promise<void> => {
  const splitReports = splitEveryMaxUpload(values(validReports));
  await Promise.all(
    splitReports.map(async (reports) => {
      googleSlideBatchUpdate(templateId, [
        ...(updateTemplateMapping(reports) as Record<string, unknown>[]),
        ...(generateSlides(reports) as Record<string, unknown>[]),
      ]);
    }),
  ).catch((err) => {
    console.error(err.message);
  });

  const dateRange = getFormattedDateRange(params);

  const deleteInvalidReports = values(deletePageMapping(invalidReports));
  const deleteBasePages = values(
    deletePageMapping(uniq(removeSegmentedBase as any)),
  );

  await googleSlideBatchUpdate(templateId, [
    ...textShapeRequestsConfig(params.merchantName as string, dateRange),
    ...deleteInvalidReports,
    ...deleteBasePages,
  ]);
};

export {
  getTemplateURL,
  allReportsHaveImages,
  uploadImages,
  getDeleteTextRequest,
  getInsertTextRequest,
  slideBatchUpdate,
};
